/**
 * `TextArea` (W1-12 ui-kit-i18n; contract TextAreaProps): a labelled multi-line text input. Enter inserts a new
 * line; Ctrl+Enter (Cmd+Enter on macOS) or blur commits when the text changed; Escape drops the edit. `code`
 * switches to a monospace, non-spellchecked editor that keeps Tab as an indent (Raw panel); Escape then Tab
 * leaves it as usual.
 */

import { useId, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useT } from '../i18n/useT';
import { useTr } from './bridge';
import { cx } from './internal/cx';
import { pendingEditProps } from './internal/layers';
import f from './internal/form.module.css';
import { useDraft } from './internal/useDraft';
import s from './TextArea.module.css';
import { textValue, type TextFieldExtraProps } from './TextField';
import type { TextAreaProps } from './types';
import { m } from './ui.messages';

const INDENT = '  ';

export function TextArea(props: TextAreaProps & Omit<TextFieldExtraProps, 'type'>) {
  const t = useT();
  const tr = useTr();
  const id = useId();
  const draft = useDraft();
  const ref = useRef<HTMLTextAreaElement>(null);
  /** After Escape, Tab moves focus instead of indenting (keyboard trap escape hatch for code mode). */
  const tabLeaves = useRef(false);
  const { text, mixed } = textValue(props.value);
  const shown = draft.text ?? text;

  const commit = (): void => {
    const d = draft.text;
    draft.clear();
    if (d !== null && (mixed || d !== text)) props.onCommit(d);
  };
  const update = (next: string): void => {
    draft.set(next);
    props.onChange?.(next);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      commit();
      return;
    }
    if (e.key === 'Escape') {
      tabLeaves.current = true;
      if (draft.text !== null) {
        e.preventDefault();
        e.stopPropagation();
        draft.clear();
        props.onChange?.(text);
      }
      return;
    }
    if (e.key === 'Tab' && props.code === true && !tabLeaves.current && !e.ctrlKey && !e.altKey && !e.metaKey) {
      const el = e.currentTarget;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const value = el.value;
      e.preventDefault();
      if (e.shiftKey) {
        const lineStart = value.lastIndexOf('\n', start - 1) + 1;
        if (value.startsWith(INDENT, lineStart)) {
          update(value.slice(0, lineStart) + value.slice(lineStart + INDENT.length));
          requestAnimationFrame(() => {
            ref.current?.setSelectionRange(
              Math.max(lineStart, start - INDENT.length),
              Math.max(lineStart, end - INDENT.length),
            );
          });
        }
        return;
      }
      update(value.slice(0, start) + INDENT + value.slice(end));
      requestAnimationFrame(() => {
        ref.current?.setSelectionRange(start + INDENT.length, start + INDENT.length);
      });
      return;
    }
    tabLeaves.current = false;
  };
  const placeholder = mixed ? t(m.mixed) : props.placeholder === undefined ? undefined : tr(props.placeholder);

  return (
    <div className={cx(f.control, props.className)} data-help-id={props.helpId}>
      <label htmlFor={id} className={cx(f.label, props.hideLabel === true && 'visually-hidden')}>
        {props.label}
      </label>
      <textarea
        ref={ref}
        id={id}
        className={cx(f.input, s.area, props.code === true && s.code)}
        rows={props.rows ?? 4}
        value={shown}
        placeholder={placeholder}
        maxLength={props.maxLength}
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        spellCheck={props.code !== true}
        autoCapitalize={props.code === true ? 'off' : undefined}
        autoCorrect={props.code === true ? 'off' : undefined}
        data-mixed={mixed && draft.text === null ? 'true' : undefined}
        onChange={(e) => {
          update(e.target.value);
        }}
        onBlur={() => {
          tabLeaves.current = false;
          commit();
        }}
        {...pendingEditProps(draft.text !== null)}
        onKeyDown={onKeyDown}
      />
    </div>
  );
}
